import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NotificationService } from '../../services/notification.service'; // NotificationService;
import { MatTableModule } from '@angular/material/table';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { firstValueFrom } from 'rxjs';
import { CollapsibleCardComponent } from '../collapsible-card/collapsible-card.component';
import {
  LoadingTableShimmerComponent,
  ShimmerColumn,
} from '../../shared/components/loading-table-shimmer/loading-table-shimmer.component';
import { TerminalSettingsService } from '../../services/terminal-settings.service';
import { SelectSearchComponent } from '../../shared/components/select-search/select-search.component';
import { SelectFilterPipe } from '../../shared/pipes/select-filter.pipe';
import {
  ConnectAccountDetails,
  DEFAULT_TERMINAL_SETTINGS,
  TerminalMenuLabels,
  TerminalReader,
} from './terminal-settings.data';

const STORAGE_KEY = 'way_terminal_settings_location';

interface LocationOption {
  id: string;
  label: string;
}

@Component({
  selector: 'app-terminal-settings',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatTableModule,
    MatSlideToggleModule,
    MatSelectModule,
    MatInputModule,
    CollapsibleCardComponent,
    LoadingTableShimmerComponent,
    SelectSearchComponent,
    SelectFilterPipe,
  ],
  providers: [NotificationService],
  templateUrl: './terminal-settings.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './terminal-settings.component.scss',
})
export class TerminalSettingsComponent implements OnInit, OnChanges {
  @Input() locations: Record<string, any>[] = [];

  readonly menuLabels = TerminalMenuLabels;
  activeSection: TerminalMenuLabels = TerminalMenuLabels.Account;

  selectedLocationId = '';
  locationOptions: LocationOption[] = [];

  loading = false;
  readersLoading = false;
  connectAccountLoading = false;
  stripeLinkLoading = false;
  accountUpdateLinkLoading = false;
  deletingConnectAccount = false;
  addingReader = false;
  updatingReader = false;

  convenienceFee = DEFAULT_TERMINAL_SETTINGS.convenienceFee;

  showAddReaderDialog = false;
  registrationCode = '';
  readerName = '';

  showEditReaderDialog = false;
  editReaderName = '';
  editingReader: TerminalReader | null = null;

  showDeleteReaderDialog = false;
  readerToDelete: TerminalReader | null = null;
  deletingReader = false;

  showDeleteAccountDialog = false;

  connectAccount: ConnectAccountDetails | null = null;
  readers: TerminalReader[] = [];

  readerColumns = [
    { field: 'label', header: 'Name', width: '200px' },
    { field: 'device_type', header: 'Device Type', width: '180px' },
    { field: 'serial_number', header: 'Serial Number', width: '200px' },
    { field: 'status', header: 'Status', width: '120px' },
    { field: 'actions', header: '', width: '60px' },
  ];

  displayedReaderColumns = ['label', 'device_type', 'serial_number', 'status', 'actions'];

  readonly accountSkeletonFields = Array.from({ length: 6 });
  readonly readerShimmerColumns: ShimmerColumn[] = [
    { label: 'Name', width: 'md' },
    { label: 'Device Type', width: 'md' },
    { label: 'Serial Number', width: 'lg' },
    { label: 'Status', width: 'sm' },
    { label: 'Actions', width: 'xs' },
  ];

  private justReturnedFromStripe = false;
  private readonly messageService = inject(NotificationService);
  private readonly terminalService = inject(TerminalSettingsService);

  ngOnInit(): void {
    const params = new URLSearchParams(window.location.search);
    if (params.get('stripe_onboarded') === '1') {
      params.delete('stripe_onboarded');
      const clean = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (clean ? `?${clean}` : ''));
      this.justReturnedFromStripe = true;
    }
    this.restoreLocationSelection();
    this.rebuildLocationOptions();
    this.ensureLocationSelected();
    this.loadConnectAccount();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const change = changes['locations'];
    if (!change) return;
    const previousLocationId = this.selectedLocationId;
    this.rebuildLocationOptions();
    this.ensureLocationSelected();
    // Initial selection/load is handled by ngOnInit. Here we react to locations
    // arriving/updating after init so the screen isn't left empty until a manual change.
    if (
      !change.firstChange &&
      this.selectedLocationId &&
      this.selectedLocationId !== previousLocationId
    ) {
      this.persistLocationSelection();
      this.loadConnectAccount();
    }
  }

  get visibleSections(): TerminalMenuLabels[] {
    const hasConnectAccount = !!this.connectAccount;
    const hasReaders = this.readers.length > 0;
    const sections = [TerminalMenuLabels.Account];
    if (hasConnectAccount) sections.push(TerminalMenuLabels.Readers);
    if (hasConnectAccount && hasReaders) sections.push(TerminalMenuLabels.Settings);
    return sections;
  }

  setActiveSection(section: TerminalMenuLabels): void {
    this.activeSection = section;
    if (section === TerminalMenuLabels.Settings) {
      this.loadTerminalSettings();
    }
  }

  onLocationChange(): void {
    this.persistLocationSelection();
    this.connectAccount = null;
    this.readers = [];
    this.activeSection = TerminalMenuLabels.Account;
    this.loadConnectAccount();
  }

  loadConnectAccount(): void {
    const locationId = this.selectedLocationId;
    if (!locationId) return;

    this.connectAccountLoading = true;
    this.terminalService.getConnectAccount(locationId).subscribe({
      next: account => {
        if (this.selectedLocationId !== locationId) return;
        this.connectAccountLoading = false;
        this.connectAccount = account;
        this.applyConnectAccountState(!!account);
      },
      error: () => {
        if (this.selectedLocationId !== locationId) return;
        this.connectAccountLoading = false;
        this.connectAccount = null;
        this.applyConnectAccountState(false);
      },
    });
  }

  loadReaders(): void {
    const locationId = this.selectedLocationId;
    if (!locationId) return;

    this.readersLoading = true;
    this.terminalService.listReaders(locationId).subscribe({
      next: rows => {
        if (this.selectedLocationId !== locationId) return;
        this.readersLoading = false;
        this.readers = rows;
        this.syncActiveSection();
      },
      error: () => {
        if (this.selectedLocationId !== locationId) return;
        this.readersLoading = false;
        this.readers = [];
        this.syncActiveSection();
      },
    });
  }

  loadTerminalSettings(): void {
    const locationId = this.selectedLocationId;
    if (!locationId) return;

    this.terminalService.getTerminalSettings(locationId).subscribe({
      next: settings => {
        this.convenienceFee = settings?.convenienceFee ?? DEFAULT_TERMINAL_SETTINGS.convenienceFee;
      },
    });
  }

  saveSettings(): void {
    const locationId = this.selectedLocationId;
    if (!locationId) return;

    this.loading = true;
    this.terminalService
      .updateTerminalSettings(locationId, { convenienceFee: this.convenienceFee })
      .subscribe({
        next: settings => {
          this.loading = false;
          if (settings) {
            this.convenienceFee = settings.convenienceFee;
            this.showToast('success', 'Saved', 'Terminal settings updated.');
          } else {
            this.showToast('error', 'Error', 'Unable to update terminal settings.');
          }
        },
        error: () => {
          this.loading = false;
          this.showToast('error', 'Error', 'Unable to update terminal settings.');
        },
      });
  }

  async createConnectAccount(): Promise<void> {
    if (this.stripeLinkLoading) return;

    const locationId = this.selectedLocationId;
    if (!locationId) {
      this.showToast('warn', 'Location required', 'Select a location before connecting Stripe.');
      return;
    }

    this.stripeLinkLoading = true;
    const basePath = `${window.location.origin}/settings?tab=Terminal`;
    const returnUrl = `${basePath}&stripe_onboarded=1`;

    try {
      const data = await firstValueFrom(
        this.terminalService.createConnectAccount(locationId, {
          returnUrl,
          refreshUrl: basePath,
        })
      );
      if (data?.url) {
        window.location.assign(data.url);
        return;
      }
      this.showToast(
        'error',
        'Stripe Connect',
        'Unable to start Stripe onboarding. Please try again.'
      );
    } catch {
      this.showToast(
        'error',
        'Stripe Connect',
        'Unable to start Stripe onboarding. Please try again.'
      );
    } finally {
      this.stripeLinkLoading = false;
    }
  }

  async editConnectAccount(): Promise<void> {
    if (this.accountUpdateLinkLoading) return;

    const locationId = this.selectedLocationId;
    if (!locationId || !this.connectAccount) {
      this.showToast('warn', 'Account required', 'Connect a Stripe account before editing.');
      return;
    }

    this.accountUpdateLinkLoading = true;
    const basePath = `${window.location.origin}/settings?tab=Terminal`;

    try {
      const data = await firstValueFrom(
        this.terminalService.createConnectAccountUpdateLink(locationId, {
          returnUrl: basePath,
          refreshUrl: basePath,
        })
      );
      if (data?.url) {
        window.location.assign(data.url);
        return;
      }
      this.showToast(
        'error',
        'Stripe Connect',
        'Unable to open Stripe account update. Please try again.'
      );
    } catch {
      this.showToast(
        'error',
        'Stripe Connect',
        'Unable to open Stripe account update. Please try again.'
      );
    } finally {
      this.accountUpdateLinkLoading = false;
    }
  }

  openDeleteAccountDialog(): void {
    if (!this.selectedLocationId || !this.connectAccount || this.deletingConnectAccount) return;
    this.showDeleteAccountDialog = true;
  }

  closeDeleteAccountDialog(): void {
    if (this.deletingConnectAccount) return;
    this.showDeleteAccountDialog = false;
  }

  confirmDeleteConnectAccount(): void {
    const locationId = this.selectedLocationId;
    if (!locationId || !this.connectAccount || this.deletingConnectAccount) return;

    this.deletingConnectAccount = true;
    this.terminalService.deleteConnectAccount(locationId).subscribe({
      next: ok => {
        this.deletingConnectAccount = false;
        this.showDeleteAccountDialog = false;
        if (ok) {
          this.connectAccount = null;
          this.readers = [];
          this.activeSection = TerminalMenuLabels.Account;
          this.showToast('success', 'Deleted', 'Connected account removed.');
        } else {
          this.showToast('error', 'Error', 'Unable to delete connected account.');
        }
      },
      error: () => {
        this.deletingConnectAccount = false;
        this.showToast('error', 'Error', 'Unable to delete connected account.');
      },
    });
  }

  openConfigureReaderDialog(): void {
    this.registrationCode = '';
    this.readerName = '';
    this.showAddReaderDialog = true;
  }

  openEditReaderDialog(reader: TerminalReader): void {
    this.editingReader = reader;
    this.editReaderName = reader.label || '';
    this.showEditReaderDialog = true;
  }

  addReader(): void {
    const locationId = this.selectedLocationId;
    if (!locationId || !this.registrationCode.trim() || !this.readerName.trim()) return;

    this.addingReader = true;
    this.terminalService
      .registerReader(locationId, {
        registrationCode: this.registrationCode.trim(),
        readerName: this.readerName.trim(),
      })
      .subscribe({
        next: reader => {
          this.addingReader = false;
          if (reader) {
            this.showAddReaderDialog = false;
            this.showToast(
              'success',
              'Reader linked',
              'Terminal reader was registered successfully.'
            );
            this.loadReaders();
          } else {
            this.showToast(
              'error',
              'Error',
              'Unable to register reader. Check the registration code and try again.'
            );
          }
        },
        error: () => {
          this.addingReader = false;
          this.showToast(
            'error',
            'Error',
            'Unable to register reader. Check the registration code and try again.'
          );
        },
      });
  }

  updateReader(): void {
    const locationId = this.selectedLocationId;
    if (!locationId || !this.editingReader?.id || !this.editReaderName.trim()) return;

    this.updatingReader = true;
    this.terminalService
      .updateReader(locationId, this.editingReader.id, this.editReaderName.trim())
      .subscribe({
        next: reader => {
          this.updatingReader = false;
          this.showEditReaderDialog = false;
          if (reader) {
            this.showToast('success', 'Updated', 'Reader name updated.');
            this.loadReaders();
          } else {
            this.showToast('error', 'Error', 'Unable to update reader.');
          }
        },
        error: () => {
          this.updatingReader = false;
          this.showToast('error', 'Error', 'Unable to update reader.');
        },
      });
  }

  openDeleteReaderDialog(reader: TerminalReader): void {
    this.readerToDelete = reader;
    this.showDeleteReaderDialog = true;
  }

  closeDeleteReaderDialog(): void {
    if (this.deletingReader) return;
    this.showDeleteReaderDialog = false;
    this.readerToDelete = null;
  }

  confirmDeleteReader(): void {
    const locationId = this.selectedLocationId;
    const reader = this.readerToDelete;
    if (!locationId || !reader?.id || this.deletingReader) return;

    this.deletingReader = true;
    this.terminalService.deleteReader(locationId, reader.id).subscribe({
      next: ok => {
        this.deletingReader = false;
        this.showDeleteReaderDialog = false;
        this.readerToDelete = null;
        if (ok) {
          this.showToast('success', 'Deleted', 'Reader removed.');
          this.loadReaders();
        } else {
          this.showToast('error', 'Error', 'Unable to delete reader.');
        }
      },
      error: () => {
        this.deletingReader = false;
        this.showToast('error', 'Error', 'Unable to delete reader.');
      },
    });
  }

  isReaderNameDuplicate(name: string, excludeId?: string): boolean {
    const trimmed = name.trim().toLowerCase();
    if (!trimmed) return false;
    return this.readers.some(r => r.label?.toLowerCase() === trimmed && r.id !== excludeId);
  }

  private applyConnectAccountState(connected: boolean): void {
    if (connected) {
      this.loadReaders();
    } else {
      this.readers = [];
      this.syncActiveSection();
    }

    if (this.justReturnedFromStripe) {
      this.justReturnedFromStripe = false;
      if (connected && this.connectAccount?.detailsSubmitted) {
        this.showToast('success', 'Stripe Connect', 'Your Stripe account is connected.');
      }
    }
  }

  private syncActiveSection(): void {
    const visible = this.visibleSections;
    if (!visible.includes(this.activeSection)) {
      this.activeSection = visible[visible.length - 1] ?? TerminalMenuLabels.Account;
    }
    if (this.activeSection === TerminalMenuLabels.Settings) {
      this.loadTerminalSettings();
    }
  }

  private rebuildLocationOptions(): void {
    this.locationOptions = (this.locations || [])
      .map(loc => {
        const id = String(loc['id'] ?? '').trim();
        const branch = String(loc['branch'] ?? '').trim();
        return id ? { id, label: branch || id } : null;
      })
      .filter((opt): opt is LocationOption => !!opt);
  }

  private ensureLocationSelected(): void {
    if (
      this.selectedLocationId &&
      this.locationOptions.some(o => o.id === this.selectedLocationId)
    ) {
      return;
    }
    const active = (this.locations || []).find(loc => loc['active'] === true);
    const activeId = String(active?.['id'] ?? '').trim();
    if (activeId && this.locationOptions.some(o => o.id === activeId)) {
      this.selectedLocationId = activeId;
      return;
    }
    this.selectedLocationId = this.locationOptions[0]?.id ?? '';
  }

  private restoreLocationSelection(): void {
    try {
      const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null;
      if (!raw) return;
      const parsed = JSON.parse(raw) as { location_id?: string };
      if (parsed?.location_id) this.selectedLocationId = parsed.location_id;
    } catch {
      /* ignore */
    }
  }

  private persistLocationSelection(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ location_id: this.selectedLocationId }));
    } catch {
      /* ignore */
    }
  }

  private showToast(severity: string, summary: string, detail: string): void {
    this.messageService.add({
      severity: (severity as 'success' | 'info' | 'warn' | 'error') || 'info',
      summary,
      detail,
      life: 4000,
    });
  }
}
